import React, { createContext, useContext } from 'react';
import * as U from '../utils/units';

interface UnitsContextValue {
  isMetric: boolean;
  distLabel: string;
  paceLabel: string;
  elevLabel: string;
  weeklyLabel: string;
  formatDist: (miles?: number, decimals?: number) => string;
  formatPace: (secPerMile?: number) => string;
  formatElev: (feet?: number) => string;
  inputDistToMiles: (v: number) => number;
  inputElevToFeet: (v: number) => number;
  milesToDisplay: (miles: number) => number;
  feetToDisplay: (feet: number) => number;
}

const UnitsContext = createContext<UnitsContextValue | null>(null);

export const UnitsProvider: React.FC<{ unitPreference?: string; children: React.ReactNode }> = ({
  unitPreference,
  children,
}) => {
  const isMetric = unitPreference === 'metric';

  const value: UnitsContextValue = {
    isMetric,
    distLabel: isMetric ? 'km' : 'mi',
    paceLabel: isMetric ? 'min/km' : 'min/mi',
    elevLabel: isMetric ? 'm' : 'ft',
    weeklyLabel: isMetric ? 'km/week' : 'mi/week',
    formatDist: (miles, decimals) => U.formatDistance(miles, isMetric, decimals),
    formatPace: (spm) => U.formatPace(spm, isMetric),
    formatElev: (feet) => U.formatElevation(feet, isMetric),
    inputDistToMiles: (v) => U.inputDistanceToMiles(v, isMetric),
    inputElevToFeet: (v) => U.inputElevationToFeet(v, isMetric),
    milesToDisplay: (miles) => U.milesToInputDisplay(miles, isMetric),
    feetToDisplay: (feet) => U.feetToInputDisplay(feet, isMetric),
  };

  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>;
};

export const useUnits = () => {
  const ctx = useContext(UnitsContext);
  if (!ctx) throw new Error('useUnits must be used within UnitsProvider');
  return ctx;
};
